import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash';
import { useOrderColumns } from './columns';

import { LicenseManager } from 'ag-grid-enterprise';

import CargomaticModal from 'components/v2/modal';
import OrderDetails from 'components/modules/orders/Module';
import { getLocaleText } from 'utils/localization/agGridLocale';
import { frameworkComponents } from './frameworkComponents';
import LoadingSkeleton from './LoadingSkeleton';
import PaginationBar from 'components/v2/PaginationBar';
import { ColumnCategoriesDropdown } from '../ColumnCategoriesDropdown';
import { perPageOptions, DEFAULT_CREATED_AT_SORT } from '../Explore';
import { useOrderData, useOrderDataCount } from 'hooks/unifiedview/orders';
import { transformFilterModelQuery } from 'components/modules/master/transformers';
import { getInitialDateRange, loadColumnState } from '../transformers';

LicenseManager.setLicenseKey(`${AG_GRID_LICENSE}`);

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

// No-op comparator: sorting is handled server-side, so we preserve the
// order of rowData as returned by the API.
const serverSideComparator = () => 0;

const gridOpts = {
  defaultColDef: {
    enableRowGroup: true,
    enablePivot: false,
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    comparator: serverSideComparator,
  },
  rowDragManaged: true,
  copyHeadersToClipboard: true,
  suppressColumnVirtualisation: true,
  suppressScrollOnNewData: true,
};

const ExploreOrderUnifiedView = forwardRef(function ExploreOrderUnifiedView(
  {
    onTotalCountChange,
    onSelectionChanged,
    onFilterChange,
    report,
    sidebarParams,
    unifiedViewV2Enabled,
  },
  ref,
) {
  // Hooks
  const { t } = useTranslation();

  const handleOrderClick = useCallback((_id) => {
    setOrderId(_id);
    setShowOrderModal(true);
  }, []);

  const columns = useOrderColumns({ onOrderClick: handleOrderClick });
  const apiRef = useRef(null);

  const [params, setParams] = useState({});
  const [options, setOptions] = useState({
    page: 1,
    per_page: 100,
    sort: undefined,
  });
  const [orderId, setOrderId] = useState('');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const initialFilterModelRef = useRef(report?.model);
  const lastFilterModelRef = useRef(null);

  useEffect(() => {
    if (report?.model) {
      initialFilterModelRef.current = report.model;
    }
  }, [report]);

  const {
    data: { items = [] } = {},
    isLoading,
    isError,
    error,
  } = useOrderData(params, options);
  const { data: totalCount = 0 } = useOrderDataCount(params);

  const onSortChanged = useCallback(() => {
    const columnsState = apiRef.current?.getColumnState() || [];
    const userSorts = columnsState.filter((col) => !!col.sort);
    setOptions((prev) => ({
      ...prev,
      sort: userSorts.length > 0 ? userSorts : [DEFAULT_CREATED_AT_SORT],
      page: 1,
    }));
  }, []);

  const setModelToInitial = () => {
    // Apply initial filter model (from saved report or default created_at)
    const filterModel =
      initialFilterModelRef.current &&
      Object.keys(initialFilterModelRef.current).length > 0
        ? initialFilterModelRef.current
        : {
            created_at: {
              ...getInitialDateRange(),
              filterType: 'date',
              type: 'inRange',
            },
          };
    apiRef.current?.setFilterModel(filterModel);
    return filterModel;
  };

  useImperativeHandle(ref, () => ({
    get api() {
      return apiRef.current;
    },
    getSelectedRows: () => {
      if (!apiRef.current) {
        return [];
      }
      return (apiRef.current.getSelectedRows() || []).map((order) => ({
        ...order,
        _id: order.order_oid,
        id: order.order_oid,
      }));
    },
    resetView: () => {
      setModelToInitial();
      applyReport();
      setTimeout(() => onFilterChanged());
    },
    queryParams: () => params,
  }));

  useEffect(() => {
    if (isError && error) {
      toast.error(`Error loading orders: ${error.message}`);
    }
  }, [isError, error]);

  useEffect(() => {
    if (onTotalCountChange) {
      onTotalCountChange(totalCount);
    }
  }, [totalCount, onTotalCountChange]);
  // need to do this because siblings of this component (bulk actions) must have
  // the latest data even if the selection is the same
  useEffect(() => {
    if (
      (apiRef.current?.getSelectedRows() || []).length > 0 &&
      onSelectionChanged
    ) {
      onSelectionChanged();
    }
  }, [items]);

  // Grid Items
  const getRowId = (params) => params.data.order_oid;
  const statusBar = {
    statusPanels: [{ statusPanel: 'agAggregationComponent' }],
  };
  const getRowHeight = () => 50;

  const onFilterChanged = useCallback(() => {
    let filterModel = apiRef.current?.getFilterModel() || {};
    if (Object.keys(filterModel).length === 0) {
      // at least the time constraint must apply
      filterModel = setModelToInitial();
    }
    if (
      lastFilterModelRef.current &&
      isEqual(lastFilterModelRef.current, filterModel)
    ) {
      return; // fix this condition
    }
    const transformedParams = transformFilterModelQuery(filterModel);
    setParams(transformedParams);
    setOptions((prev) => ({ ...prev, page: 1 }));
    lastFilterModelRef.current = filterModel;
    onFilterChange(filterModel); // so parent can render chips
  }, []);

  const applyReport = () => {
    if (report) {
      loadColumnState(report, apiRef.current, [], unifiedViewV2Enabled);

      if (report.rowGroupColumns?.length > 0) {
        apiRef.current.setRowGroupColumns(report.rowGroupColumns);
      }
    }
  };

  const onGridReady = (params) => {
    const { api } = params;
    apiRef.current = api;
    api.autoSizeColumns();

    const filterModel = setModelToInitial();
    const transformedParams = transformFilterModelQuery(filterModel);
    setParams(transformedParams);
    setOptions((prev) => ({ ...prev, page: 1 }));
    lastFilterModelRef.current = filterModel;

    applyReport();
  };

  const showInitialLoading = isLoading && items.length === 0;

  const renderPagination = () => {
    return (
      <PaginationBar
        data-testid="pagination-bar-uv-orders"
        total={totalCount}
        page={options.page}
        perPage={options.per_page}
        perPageOptions={perPageOptions}
        onPageClick={(page) => setOptions((prev) => ({ ...prev, page }))}
        onPerPageChange={(value) =>
          setOptions((prev) => ({ ...prev, per_page: Number(value), page: 1 }))
        }
      >
        <ColumnCategoriesDropdown api={apiRef.current} />
      </PaginationBar>
    );
  };
  return (
    <div data-testid="div-da5f52c9" className="h-full w-full">
      {showInitialLoading && <LoadingSkeleton />}
      <div
        className={
          showInitialLoading ? 'invisible h-0 overflow-hidden' : 'h-full'
        }
      >
        {renderPagination()}
        <div className="px-8 pt-4 h-full">
          <AgGridReact
            rowData={items}
            maintainColumnOrder
            rememberGroupStateWhenNewData
            animateRows
            getRowHeight={getRowHeight}
            getRowId={getRowId}
            suppressClickEdit
            suppressRowClickSelection
            enableCellTextSelection
            enableRangeSelection
            rowSelection="multiple"
            components={frameworkComponents}
            multiSortKey="ctrl"
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            statusBar={statusBar}
            columnDefs={columns}
            defaultColDef={gridOpts.defaultColDef}
            onSelectionChanged={onSelectionChanged}
            localeText={getLocaleText()}
            overlayLoadingTemplate={`<div><span class="text-base text-green font-bold">${t(
              'LOADING',
            )}</span></div>`}
            sideBar={sidebarParams}
            allowDragFromColumnsToolPanel
          />
        </div>
      </div>
      {showOrderModal && (
        <CargomaticModal
          dataTestId="uv-orders-order-modal"
          className="w-full"
          show
          onHide={() => setShowOrderModal(false)}
          title={titleize(t('DETAIL', { count: 2 }))}
          body={<OrderDetails renderedInModal id={orderId} />}
          footer={null}
          location="top"
          dialogClassName="w-11/12"
        />
      )}
    </div>
  );
});

export default ExploreOrderUnifiedView;
